import { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../store';
import { selectOfflineSyncSummary } from '../store/slices/offlineSlice';
import { syncService } from '../services/syncService';

export const useSync = () => {
  const offlineSync = useSelector((state: RootState) => selectOfflineSyncSummary(state), shallowEqual);
  const [lastSyncStatus, setLastSyncStatus] = useState<'success' | 'error' | 'pending' | null>(null);

  // Initialize sync service on mount
  useEffect(() => {
    syncService.initialize();
  }, []);

  // Monitor sync progress
  useEffect(() => {
    if (offlineSync.syncInProgress) {
      setLastSyncStatus('pending');
    } else if (offlineSync.lastSyncTime) {
      setLastSyncStatus('success');
    }
  }, [offlineSync.syncInProgress, offlineSync.lastSyncTime]);

  const forceSync = async () => {
    try {
      setLastSyncStatus('pending');
      await syncService.forcSync();
      setLastSyncStatus('success');
    } catch (error) {
      setLastSyncStatus('error');
      throw error;
    }
  };

  const clearOfflineData = async () => {
    await syncService.clearOfflineData();
  };

  const getSyncStatusText = () => {
    const actionLabel = `${offlineSync.pendingActionsCount} action${offlineSync.pendingActionsCount !== 1 ? 's' : ''}`;

    if (!offlineSync.isOnline) {
      return offlineSync.pendingActionsCount > 0
        ? `Offline - ${actionLabel} waiting to sync`
        : 'Offline';
    }
    
    if (offlineSync.syncInProgress) {
      return 'Syncing...';
    }
    
    if (offlineSync.pendingActionsCount > 0) {
      return `${actionLabel} waiting to sync`;
    }
    
    if (offlineSync.lastSyncTime) {
      const lastSync = new Date(offlineSync.lastSyncTime);
      const now = new Date();
      const diff = now.getTime() - lastSync.getTime();
      const minutes = Math.floor(diff / 60000);
      
      if (minutes === 0) {
        return 'Synced just now';
      } else if (minutes === 1) {
        return 'Synced 1 minute ago';
      } else {
        return `Synced ${minutes} minutes ago`;
      }
    }
    
    return 'Online';
  };

  const getConnectionStatus = () => {
    return {
      isOnline: offlineSync.isOnline,
      isConnected: offlineSync.isOnline,
      hasInternet: offlineSync.isOnline,
    };
  };

  return {
    // State
    isOnline: offlineSync.isOnline,
    pendingActionsCount: offlineSync.pendingActionsCount,
    syncInProgress: offlineSync.syncInProgress,
    lastSyncTime: offlineSync.lastSyncTime,
    lastSyncStatus,
    
    // Actions
    forceSync,
    clearOfflineData,
    
    // Helpers
    getSyncStatusText,
    getConnectionStatus,
    
    // Queue actions
    queueReviewUpdate: syncService.queueReviewUpdate.bind(syncService),
    queueReviewSubmission: syncService.queueReviewSubmission.bind(syncService),
    queuePhotoUpload: syncService.queuePhotoUpload.bind(syncService),
    queuePhotoDelete: syncService.queuePhotoDelete.bind(syncService),
    queueReviewCreation: syncService.queueReviewCreation.bind(syncService),
  };
};
